import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2Icon, MonitorPlayIcon, PlayIcon } from 'lucide-react';
import type { Slot } from '../types/slot';
import { getDemoUrl } from '../utils/slots';
import { trackEvent } from '../utils/analytics';
import { SlotThumb } from './SlotThumb';
type DemoState = 'idle' | 'loading' | 'ready' | 'unavailable';
export function DemoFrame({
  slot,
  autoStart = false
}: {
  slot: Slot;
  autoStart?: boolean;
}) {
  const demoUrl = getDemoUrl(slot);
  const initial: DemoState = autoStart ? demoUrl ? 'loading' : 'unavailable' : 'idle';
  const [state, setState] = useState<DemoState>(initial);
  const track = (source: 'modal' | 'inline') => trackEvent('demo_start', {
    slot_name: slot.name,
    slot_rank: slot.rank,
    provider: slot.provider,
    demo_available: Boolean(demoUrl),
    source
  });
  useEffect(() => {
    if (autoStart) track('modal');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const start = () => {
    track('inline');
    setState(demoUrl ? 'loading' : 'unavailable');
  };
  return <div className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl bg-ink sm:aspect-video" data-id="element-22" data-mp="1zf7idsixmk97">
      {(state === 'loading' || state === 'ready') && demoUrl && <iframe src={demoUrl} title={`${slot.name} gratis demo`} className="absolute inset-0 h-full w-full border-0" allow="fullscreen; autoplay" onLoad={() => setState('ready')} data-id="element-23" data-mp="1qe9b91d75wme" />}

      <AnimatePresence data-id="element-24" data-mp="2i68e87or9zoj">
        {state === 'idle' && <motion.div key="idle" initial={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18
      }} className="absolute inset-0 flex flex-col items-center justify-center gap-5 p-6 text-center" style={{
        backgroundColor: slot.color
      }} data-id="element-25" data-mp="24c7qsa397z3h">
            <SlotThumb slot={slot} size="lg" data-id="element-26" data-mp="31uhk1kj8246i" />
            <div data-id="element-27" data-mp="3bon2ugjhp63v">
              <p className="font-serif text-2xl font-semibold text-white" data-id="element-28" data-mp="3bn29gu8wvt7l">{slot.name}</p>
              <p className="mt-1 text-sm text-white/80" data-id="element-29" data-mp="36qbt05t72vln">Gratis demo · speelgeld · geen account nodig</p>
            </div>
            <button type="button" onClick={start} className="inline-flex h-12 items-center gap-2 rounded-full bg-white px-6 font-semibold text-ink transition-transform duration-150 ease-out hover:scale-[1.02] active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent" data-id="element-30" data-mp="31omh6ny5edwi">
              <PlayIcon className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-31" data-mp="2ixlky7oewwgn" />
              Start gratis demo
            </button>
          </motion.div>}

        {state === 'loading' && <motion.div key="loading" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink text-white/80" role="status" data-id="element-32" data-mp="293mj79s93ep8">
            <Loader2Icon className="h-6 w-6 animate-spin" aria-hidden="true" data-id="element-33" data-mp="tiatdxilhrko" />
            <p className="text-sm" data-id="element-34" data-mp="yhf3qrh99vcl">Demo van {slot.provider} laden…</p>
          </motion.div>}
      </AnimatePresence>

      {state === 'unavailable' && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink p-6 text-center text-white" data-id="element-35" data-mp="vq2sz9y1ji6">
          <MonitorPlayIcon className="h-8 w-8 text-white/60" aria-hidden="true" data-id="element-36" data-mp="as2xo0h863ei" />
          <p className="font-serif text-xl font-semibold" data-id="element-37" data-mp="apzx4mig4rvj">Demo binnenkort beschikbaar</p>
          <p className="max-w-sm text-sm text-white/70" data-id="element-38" data-mp="fk45993g75y7">
            {slot.provider} levert de demo van {slot.name} nog niet voor Nieuw-Zeeland. Je kunt de slot wel proberen in
            de demomodus van een van de casino’s hieronder.
          </p>
        </div>}
    </div>;
}